import { NgClass } from '@angular/common';
import {
  Component,
  computed, effect, inject,
  signal,
  viewChild
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastItem } from 'src/app/common/toast/toast.component';
import { CurrentUser } from 'src/app/interfaces/current-user';
import { User } from 'src/app/interfaces/user';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { ModalsService } from 'src/app/services/Modals/modals.service';
import { ToastService } from 'src/app/services/Toast/toast.service';
import { UserService } from 'src/app/services/User/user.service';
import { ButtonComponent } from 'src/app/common/button/button.component';
import { PageTitleComponent } from 'src/app/common/page-title/page-title.component';
import { SpinnerComponent } from 'src/app/common/spinner/spinner.component';
import { FaeDetailsComponent } from '../common/fae-details/fae-details.component';
import { PersonalInformationComponent } from 'src/app/shared/account/profile/common/personal-information/personal-information.component';
import { ProfileImageUploadComponent } from 'src/app/shared/account/profile/common/profile-image-upload/profile-image-upload.component';
import { QrCodeComponent } from 'src/app/common/qr-code/qr-code.component';
import { TSMDetails } from '../common/tsm-details/tsm-details.component';

@Component({
  selector: 'internal-profile',
  imports: [
    PageTitleComponent,
    PersonalInformationComponent,
    ReactiveFormsModule,
    SpinnerComponent,
    ButtonComponent,
    ProfileImageUploadComponent,
    NgClass,
    FaeDetailsComponent,
    QrCodeComponent,
    TSMDetails,
  ],
  templateUrl: './internal.component.html',
  styleUrl: './internal.component.scss',
})
export class InternalComponent {
  // views
  upload_modal = viewChild<ProfileImageUploadComponent>('upload_modal');
  fae_form = viewChild<FaeDetailsComponent>('fae_form');
  tsm_form = viewChild<TSMDetails>('tsm_form');

  // injectables
  private fb = inject(FormBuilder);
  private userApi = inject(UserService);
  private auth = inject(AuthService);
  private toast = inject(ToastService);
  private modal = inject(ModalsService);

  // states
  editing = signal<boolean>(false);
  loading = signal<boolean>(false);
  saving = signal<boolean>(false);

  // data
  user = signal<User>({} as User);
  personal_information_form!: FormGroup;
  role!: string

  // computes
  isProcessingImage = computed(() => this.withProcessing());

  constructor() {
    this.initPersonalInformationForm();
  }

  ngOnInit() {
    this.role = this.auth.getCurrentRole()!;
    this.fetchUser();
  }

  fetchUser() {
    const getProfile = this.role == 'fae'
    ? this.userApi.viewFaeProfile()
    : this.userApi.viewProfile();
    
    this.loading.set(true)
    getProfile.subscribe((val) => {
      this.loadUser(val);
      this.loading.set(false)
    });
  }

  loadUser(user: User) {
    this.user.set(user);
    this.personal_information_form.patchValue(user);
    const currentUser = this.auth.getCurrentUser() ?? ({} as CurrentUser);

    this.auth.setUser({
      ...currentUser,
      first_name: user.first_name,
      middle_name: user.middle_name,
      last_name: user.last_name,
      image: user.image,
    });
  }

  initPersonalInformationForm() {
    this.personal_information_form = this.fb.group({
      first_name: [
        this.user().first_name,
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern('^[a-zA-ZÑñ ]*$'),
        ],
      ],
      middle_name: [
        this.user().middle_name ?? '',
        [Validators.maxLength(50), Validators.pattern('^[a-zA-ZÑñ ]*$')],
      ],
      last_name: [
        this.user().last_name,
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern('^[a-zA-ZÑñ ]*$'),
        ],
      ],
      suffix: [this.user().suffix ?? '', [Validators.maxLength(10)]],
      mobile: [
        this.user().mobile,
        [
          Validators.required,
          Validators.pattern('^\\+639[0-9]{9}$'),
          Validators.maxLength(13),
        ],
      ],
    });
  }

  save() {
    if (!this.validForms()) return;
    const loading = this.toast.makeStaticToast(
      'loading',
      'Updating profile information',
    );
    
    // prep form
    const formData = new FormData();

    const values = this.personal_information_form.value;

    Object.keys(values).map((x) => {
      formData.append(x, values[x] || '');
    });

    if (this.role == 'fae') {
      // load fae details
      const faeValues = this.fae_form()?.getForm().getRawValue();
      if (faeValues) {
        formData.append('category', faeValues.category || '');
        formData.append('fae_abbr', faeValues.fae_abbr || '');
      }

      const file = this.fae_form()?.e_signature();

      if (file && typeof file !== 'string') {
        formData.append('e_signature', file);
      }
    }

    if (this.role == 'tsm' || this.role == 'ts') {
       const file = this.tsm_form()?.e_signature();

       if (file && typeof file !== 'string') {
         formData.append('e_signature', file);
       }
    }

    this.saving.set(true);
    const getProfileUpdate = this.role == 'fae'
      ? this.userApi.updateFaeProfile(formData)
      : this.userApi.updateProfile(formData);

    getProfileUpdate.subscribe({
      next: () => this.onSuccess(loading, 'Profile updated successfully!'),
      error: () => this.onError(loading, 'Failed to update profile!'),
    });
  }

  // utils
  toggleEditing() {
    this.editing.update((x) => !x);
    if (!this.editing()) { // reset field values
      this.fae_form()?.loadUser(this.user())
      this.personal_information_form.patchValue(this.user())
      this.tsm_form()?.loadValues()
    }
  }

  validForms(): boolean {
    if (this.personal_information_form.invalid) {
      this.toast.makeToast(
        'error',
        'Please complete all required fields',
        5000,
      );
      return false;
    }
    return true;
  }

  preventDefaults(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  onSuccess(toast: ToastItem, message: string) {
    this.fetchUser();
    this.toast.removeToast(toast);
    this.toast.makeToast('success', message, 5000);
    this.saving.set(false);
    this.editing.set(false);
  }

  onError(toast: ToastItem, message: string) {
    this.toast.removeToast(toast);
    this.toast.makeToast('error', message, 5000);
    this.saving.set(false)
  }

  confirmRemovePhoto(event: Event) {
    this.preventDefaults(event);
    this.modal
      .getInstance()
      .showConfirm(
        'Remove Profile Photo',
        'Are you sure you want to remove your profile photo?',
        'Confirm',
        'Cancel',
        () => this.removePhoto(),
      );
  }

  removePhoto() {
    const loading = this.toast.makeStaticToast(
      'loading',
      'Removing profile photo',
    );
    this.userApi.removeProfileImage().subscribe({
      error: () => this.onError(loading, 'Failed to remove profile photo'),
      complete: () => {
        this.fetchUser();
        this.toast.removeToast(loading);
      },
    });
  }

  updateProfileImage(file: File) {
    const loading = this.toast.makeStaticToast(
      'loading',
      'Updating profile photo',
    );
    this.userApi.uploadProfileImage(file).subscribe({
      next: () => {
        this.onSuccess(loading, 'Profile photo updated successfully');
        this.editing.set(false);
      },
      error: () => this.onError(loading, 'Failed to update profile photo'),
    });
  }

  generateQRData() {
    const encoded = btoa(this.user().uuid);
    return `${window.location.origin}/register?invite=${encoded}`;
  }

  withProcessing() {
    return (
      this.tsm_form()?.isProcessing() ||
      this.fae_form()?.isProcessing() ||
      false
    );
  }
}
